import qs from 'query-string';

export interface IHeaders {
  Accept?: string;
  'Content-Type'?: string;
  Authorization?: string;
}

export default class BaseApi {
  private _headers: IHeaders;

  private _authHeader?: { Authorization: string };

  constructor(authToken?: string) {
    this._headers = {
      Accept: 'application/json',
    };
    if (authToken) {
      this._authHeader = {
        Authorization: `Bearer ${authToken}`,
      };
    }
  }

  protected async get<T>(
    endpoint: string,
    options?: {
      params?: Record<string, unknown>;
      customHeaders?: IHeaders;
      auth?: string;
    },
  ): Promise<T> {
    const url = this._generateLink(endpoint, options?.params);
    const headers = this._generateHeaders(options?.customHeaders);

    const response = await fetch(url, {
      method: 'GET',
      headers: { ...headers },
    });

    return response.json();
  }

  protected async post<P, T>(
    endpoint: string,
    payload: P,
    options?: {
      params?: Record<string, unknown>;
      customHeaders?: IHeaders;
      auth?: string;
    },
    removeContentType?: boolean,
  ): Promise<T> {
    const url = this._generateLink(endpoint, options?.params);

    const headers = this._generateHeaders(
      options?.customHeaders,
      removeContentType,
    );

    const response = await fetch(url, {
      method: 'POST',
      headers: { ...headers },
      body: JSON.stringify(payload),
    });

    return response.json();
  }

  protected async delete<P, T>(
    endpoint: string,
    payload: P,
    options?: {
      params?: Record<string, unknown>;
      customHeaders?: IHeaders;
      auth?: string;
    },
  ): Promise<T> {
    const url = this._generateLink(endpoint, options?.params);
    const headers = this._generateHeaders(options?.customHeaders);

    const response = await fetch(url, {
      method: 'DELETE',
      headers: { ...headers },
      body: JSON.stringify(payload),
    });

    return response.json();
  }

  protected async put<P, T>(
    endpoint: string,
    payload: P,
    options?: {
      params?: Record<string, unknown>;
      customHeaders?: IHeaders;
      auth?: string;
    },
  ): Promise<T> {
    const url = this._generateLink(endpoint, options?.params);
    const headers = this._generateHeaders(options?.customHeaders);

    const response = await fetch(url, {
      method: 'PUT',
      headers: { ...headers },
      body: JSON.stringify(payload),
    });

    return response.json();
  }

  private _generateLink(
    endpoint: string,
    params?: Record<string, unknown>,
  ): string {
    let queryParams;
    if (params) {
      queryParams = qs.stringify(params);
    }
    return `${endpoint}${queryParams ? `?${queryParams}` : ''}`;
  }

  private _generateHeaders(
    customHeaders?: IHeaders,
    removeContentType?: boolean,
  ): IHeaders {
    const headers: IHeaders = {
      ...this._headers,
      ...customHeaders,
      ...(this._authHeader && this._authHeader),
      'Content-Type': !removeContentType ? 'application/json' : undefined,
    };
    return headers;
  }
}
